import * as CONSTANTS from "./constants";
import { DirectedAttack } from "./DirectedAttack";
import { Storage } from "./Storage";
import { ActionLogger } from "./ActionLogger";

/**
 * Manages attacks - both INFECTED_SPREAD and INFECTED_DIRECTED
 * INFECTED_DIRECTED attack path finding is delegated to the DirectedAttack class 
 */
export class AttackManager {
    /** array of directed attacks. Logic for managing the trails has been moved into the DirectedAttack class in DirectedAttack.php
     * @type {DirectedAttack[]}
     */
    directedAttacks;
    initialSpreadNodes;
    seed;

    constructor(seed = 4942) {
        this.seed = seed;
        this.directedAttacks = [];
        this.initialSpreadNodes = [];
    }

    /**
     * Add attacks to the graph by infecting a node with the given @param attackType
     * @param {Storage} storage 
     * @param {string} attackType
     * @param {number} round
     */
    injectAttack(storage, attackType, round) {
        let logger = ActionLogger.getLogger();
        let names = storage.getNodeNames();
        let allPickedNodes = [];
        allPickedNodes = storage.getNodesByStatus(CONSTANTS.COMPROMISED);

        if (attackType === CONSTANTS.INFECTED_SPREAD) {
            names = storage.getNodesByStatus(CONSTANTS.HEALTHY);
            let nodesToInfect = this.getRandomIsolatedNode(names, allPickedNodes);
            this.initialSpreadNodes.push(nodesToInfect);
            // select two roles for this attack to be visible to (this seems like all roles)
            storage.updateNodeStatus(nodesToInfect, CONSTANTS.ALL_ROLES, CONSTANTS.INFECTED_SPREAD, round);
            logger.log('new spread infection from ' + this.initialSpreadNodes[this.initialSpreadNodes.length - 1]);
            logger.log('initial point(s) of spread infection: ' + JSON.stringify(this.initialSpreadNodes));
        }
        else if (attackType === CONSTANTS.INFECTED_DIRECTED) {
            logger.warn('only one direct attack is supported');

            // The following comment is not what is currently being done. It would be better for gameplay, but is more complicated and prone to bugs so for now it's being removed.
            // pick nodes at least 4 hops away from the initial spread node are applicable since it will take at least 2
            // rounds before the directed attack could potentially be cut off by the spread attack
            if (this.initialSpreadNodes) {
                names = storage.getNodesByStatus(CONSTANTS.HEALTHY);
                this.initialDirectedNode = this.getRandomIsolatedNode(names, allPickedNodes);
                // select two roles for this attack to be visible to
                let directedVisibleRoles = this.getRandomValues(CONSTANTS.ALL_ROLES, 2);
                storage.updateNodeStatus(this.initialDirectedNode, directedVisibleRoles, CONSTANTS.INFECTED_DIRECTED, round);
                this.directedAttacks.push(DirectedAttack.build(this.initialDirectedNode));
                logger.log('new directed infection from ' + this.initialDirectedNode);
                logger.log('initial point(s) of directed infection: ' + JSON.stringify(this.initialDirectedNode));
                // spreading attacks don't have to be tracked but directed attacks do
            }
        }
        else {
            throw new Error("Unsupported Attack Type - please choose INFECTED_DIRECTED or INFECTED_SPREAD");
        }
    }

    /** get a node name that hasn't been picked yet or neighbors a picked node 
     * @param {Node[]} NODES
     * @param {Node[]} allPickedNodes
     * @return {string} attackedNode
     */
    getRandomIsolatedNode(NODES, allPickedNodes) {
        let nodes = Object.values(NODES);
        nodes = nodes.sort((a, b) => 0.5 - Math.random());
        let i = 0;
        let attackedNode = null
        do {
            attackedNode = nodes[i++];
        } while (allPickedNodes.includes(attackedNode));
        return attackedNode;
    }

    /**
     * Find all nodes infected with INFECTED_SPREAD and infect all healthy neighbors
     * @param {number} round
     * @param {Storage} storage
     */
    advanceSpreadAttacks(round, storage) {
        let logger = ActionLogger.getLogger();
        logger.log('advancing spread attacks');
        let roles = CONSTANTS.ALL_ROLES;
        let spreadInfectedList = storage.getNodesByStatus(CONSTANTS.INFECTED_SPREAD);

        let newlyInfectedNodeNames = [];

        for (let sourceNode of spreadInfectedList) {
            for (let node of storage.getNodeNeighborDatas(sourceNode)) {
                node = storage.getNodeData(node.getName());
                if (node.isHealthy()) {
                    newlyInfectedNodeNames.push(node.getName());
                    storage.updateNodeStatus(node.getName(), node.getAttackVisibility(), CONSTANTS.INFECTED_SPREAD, round);
                }
            }
        }

        for (let node of newlyInfectedNodeNames) {
            let attackVisibility = this.getRandomValues(roles, 2);
            for (let role of attackVisibility) {
                storage.appendRoleToAttackVisibility(node, role);
            }
        }
        logger.log('newly infected nodes: ' + JSON.stringify(newlyInfectedNodeNames));
    }

    /**
     * Get all directed attacks and advance their spread. Logic for spread path is delegated to DirectedAttack.js
     * @param {number} round
     * @param {Storage} storage
     */
    advanceDirectedAttacks(round, storage) {
        let logger = ActionLogger.getLogger();
        logger.log('Advancing directed attacks...');
        for (let directedAttack of this.directedAttacks) {
            let neighborToInfect = this.getNeighborToInfect(directedAttack, storage);
            if (neighborToInfect === null) continue;
            storage.updateNodeStatus(neighborToInfect, CONSTANTS.ALL_ROLES, CONSTANTS.INFECTED_DIRECTED, round);
            logger.log('infected directed node: ' + neighborToInfect);
            directedAttack.advanceTrail(neighborToInfect);
        }
    }

    /**
     * Handles logic for finding a neighbor to infect and delegates backtracking to DirectedAttack.php
     * @param {DirectedAttack} directedAttack
     * @param {Storage} storage
     * @return {string | null} returns null if nothing is found
     */
    getNeighborToInfect(directedAttack, storage) {
        let logger = ActionLogger.getLogger();
        let shouldContinue = false;
        let uninfectedNeighbors = [];
        do {
            let attackingNode = directedAttack.getAttackingNode(storage);
            if (attackingNode === null) return null;
            let neighbors = storage.getNodeNeighborDatas(attackingNode);
            for (let neighbor of neighbors) {
                let status = neighbor.getStatus();
                let edgeStatus = storage.getEdgeStatus(attackingNode, neighbor.getName());
                if (status === CONSTANTS.HEALTHY && edgeStatus === CONSTANTS.UP) {
                    uninfectedNeighbors.push(neighbor.getName());
                }
            }
            if (uninfectedNeighbors.length === 0) {
                // If it successfully backtracks, then we try again after backtracking. It will fail if there are no nodes to backtrack to.
                shouldContinue = directedAttack.backtrack();
            }
        } while (shouldContinue);
        if (uninfectedNeighbors.length === 0) return null;
        else {
            return uninfectedNeighbors[Math.floor(Math.random() * uninfectedNeighbors.length)];
        }
    }

    /**
     * get random values from @param ARR
     * @param ARR
     * @param num = 1 | number of items to get from array
     */
    getRandomValues(ARR, num = 1) {
        if (ARR.length === 0) {
            return [];
        }
        let arr = Object.values(ARR);
        arr = arr.sort((a, b) => 0.5 - Math.random());
        if (num == 1) { // TODO: This is different from the PHP (potentially a mistake in PHP code)
            return [arr[0]];
        }
        else {
            let returnVals = [];
            for (let i = 0; i < num; ++i) {
                returnVals.push(arr[i]);
            }
            return returnVals;
        }
    }

    toJSON() {
        return {
            directed_attacks: this.directedAttacks,
            initial_spread_nodes: this.initialSpreadNodes,
            seed: this.seed
        };
    }

    static jsonDeserialize(jsonString) {
        let data = jsonString;
        let instance = new AttackManager();
        instance.directedAttacks = data.directed_attacks.map(json => {
            return DirectedAttack.jsonDeserialize(json);
        });
        instance.initialSpreadNodes = data.initial_spread_nodes;
        instance.seed = data.seed;
        return instance;
    }
}